import * as React from "react";
import AnalyticsChart, { ChartData } from "../AnalyticsChart";
import { ChartComponentProps, SubscriptionData, AnalyticsDataPoint } from "../../types/stripe";
import { AnalyticsChartSpinner } from "../shared";

/**
 * MRRChart Component
 * 
 * Monthly Recurring Revenue visualization with growth indicators and forecasting
 */
interface MRRChartProps extends ChartComponentProps {
  data: SubscriptionData;
  showForecast?: boolean;
  showGrowthRate?: boolean;
  currency?: string;
  chartType?: 'line' | 'area' | 'bar';
}

const MRRChart: React.FC<MRRChartProps> = ({
  data,
  showForecast = true,
  showGrowthRate = true,
  currency = 'USD',
  chartType = 'area',
  isLoading = false,
  error = null,
  height = 400,
  title = "Monthly Recurring Revenue (MRR)",
  showLegend = true,
  onDataPointClick,
  className = ""
}) => {
  const [selectedMetrics, setSelectedMetrics] = React.useState<Set<string>>(
    new Set(['mrr', 'arr'])
  );
  const [viewMode, setViewMode] = React.useState<'absolute' | 'growth'>('absolute');

  const formatCurrency = React.useCallback((value: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  }, [currency]);

  const formatPercentage = React.useCallback((value: number): string => {
    return `${value > 0 ? '+' : ''}${value.toFixed(1)}%`;
  }, []);

  // Calculate growth rates
  const calculateGrowthRates = (dataPoints: AnalyticsDataPoint[]): AnalyticsDataPoint[] => {
    return dataPoints.map((point, index) => {
      if (index === 0) return { ...point, value: 0 };
      
      const previousValue = dataPoints[index - 1].value;
      const growthRate = previousValue > 0 ? ((point.value - previousValue) / previousValue) * 100 : 0;
      
      return {
        ...point,
        value: growthRate
      };
    });
  };

  // Prepare chart data
  const chartData: ChartData = React.useMemo(() => {
    if (!data || Object.keys(data).length === 0) {
      return { labels: [], datasets: [] };
    }

    const labels = data.mrr?.map(point => {
      const date = new Date(point.date);
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        year: '2-digit'
      });
    }) || [];

    const datasets = [];

    // Color scheme
    const colors = {
      mrr: { border: '#0d6efd', background: 'rgba(13, 110, 253, 0.1)' },
      arr: { border: '#198754', background: 'rgba(25, 135, 84, 0.1)' },
      new_subscriptions: { border: '#20c997', background: 'rgba(32, 201, 151, 0.1)' },
      canceled_subscriptions: { border: '#dc3545', background: 'rgba(220, 53, 69, 0.1)' },
      growth_rate: { border: '#fd7e14', background: 'rgba(253, 126, 20, 0.1)' }
    };

    if (viewMode === 'absolute') {
      // MRR dataset
      if (selectedMetrics.has('mrr') && data.mrr) {
        datasets.push({
          label: 'MRR',
          data: data.mrr.map(point => point.value),
          borderColor: colors.mrr.border,
          backgroundColor: chartType === 'area' ? colors.mrr.background : colors.mrr.border,
          borderWidth: 3,
          fill: chartType === 'area',
          tension: 0.4,
          pointRadius: 4,
          pointHoverRadius: 6
        });
      }

      // ARR dataset (scaled down for visibility)
      if (selectedMetrics.has('arr') && data.arr) {
        datasets.push({
          label: 'ARR (scaled)',
          data: data.arr.map(point => point.value / 12), // Convert to monthly equivalent
          borderColor: colors.arr.border,
          backgroundColor: chartType === 'area' ? colors.arr.background : colors.arr.border,
          borderWidth: 2,
          fill: chartType === 'area',
          tension: 0.4,
          pointRadius: 3,
          pointHoverRadius: 5,
          yAxisID: 'y1'
        });
      }

      // New subscriptions
      if (selectedMetrics.has('new_subscriptions') && data.new_subscriptions) {
        const maxMRR = Math.max(...(data.mrr?.map(p => p.value) || [1]));
        const maxNew = Math.max(...data.new_subscriptions.map(p => p.value));
        const scale = maxMRR / (maxNew || 1) * 0.2; // Scale to 20% of MRR range

        datasets.push({
          label: 'New Subscriptions (scaled)',
          data: data.new_subscriptions.map(point => point.value * scale),
          borderColor: colors.new_subscriptions.border,
          backgroundColor: chartType === 'area' ? colors.new_subscriptions.background : colors.new_subscriptions.border,
          borderWidth: 2,
          fill: chartType === 'area',
          tension: 0.4,
          pointRadius: 3,
          pointHoverRadius: 5
        });
      }

      // Canceled subscriptions (negative)
      if (selectedMetrics.has('canceled_subscriptions') && data.canceled_subscriptions) {
        datasets.push({
          label: 'Churn Impact',
          data: data.canceled_subscriptions.map(point => -point.value),
          borderColor: colors.canceled_subscriptions.border,
          backgroundColor: chartType === 'area' ? colors.canceled_subscriptions.background : colors.canceled_subscriptions.border,
          borderWidth: 2,
          fill: chartType === 'area',
          tension: 0.4,
          pointRadius: 3,
          pointHoverRadius: 5
        });
      }
    } else {
      // Growth rate view
      if (data.mrr && data.mrr.length > 1) {
        const growthRates = calculateGrowthRates(data.mrr);
        datasets.push({
          label: 'MRR Growth Rate',
          data: growthRates.map(point => point.value),
          borderColor: colors.growth_rate.border,
          backgroundColor: chartType === 'area' ? colors.growth_rate.background : colors.growth_rate.border,
          borderWidth: 3,
          fill: chartType === 'area',
          tension: 0.4,
          pointRadius: 4,
          pointHoverRadius: 6
        });
      }
    }

    return { labels, datasets };
  }, [data, selectedMetrics, viewMode, chartType]);

  const chartOptions = React.useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index' as const,
      intersect: false,
    },
    plugins: {
      legend: {
        display: showLegend,
        position: 'top' as const,
      },
      tooltip: {
        callbacks: {
          label: (context: any) => {
            let label = context.dataset.label || '';
            if (label) label += ': ';
            
            if (viewMode === 'growth') {
              label += formatPercentage(context.parsed.y);
            } else if (context.dataset.label?.includes('ARR')) {
              label += formatCurrency(Math.abs(context.parsed.y * 12)); // Convert back to annual
            } else {
              label += formatCurrency(Math.abs(context.parsed.y));
            }
            
            return label;
          }
        }
      },
    },
    scales: {
      x: {
        display: true,
        title: {
          display: true,
          text: 'Month'
        },
        grid: {
          display: true,
          color: 'rgba(0, 0, 0, 0.1)'
        }
      },
      y: {
        type: 'linear' as const,
        display: true,
        position: 'left' as const,
        title: {
          display: true,
          text: viewMode === 'growth' ? 'Growth Rate (%)' : `Revenue (${currency})`
        },
        ticks: {
          callback: function(value: any) {
            return viewMode === 'growth' ? formatPercentage(value) : formatCurrency(value);
          }
        },
        grid: {
          display: true,
          color: 'rgba(0, 0, 0, 0.1)'
        }
      },
      y1: viewMode === 'absolute' && selectedMetrics.has('arr') ? {
        type: 'linear' as const,
        display: true,
        position: 'right' as const,
        title: {
          display: true,
          text: `ARR (${currency})`
        },
        ticks: {
          callback: function(value: any) {
            return formatCurrency(value * 12);
          }
        },
        grid: {
          drawOnChartArea: false,
        },
      } : undefined
    }
  }), [showLegend, formatCurrency, formatPercentage, currency, viewMode, selectedMetrics]);

  const handleMetricToggle = (metric: string) => {
    const newSelected = new Set(selectedMetrics);
    if (newSelected.has(metric)) {
      if (newSelected.size > 1 || viewMode === 'growth') {
        newSelected.delete(metric);
      }
    } else {
      newSelected.add(metric);
    }
    setSelectedMetrics(newSelected);
  };

  const availableMetrics = [
    { key: 'mrr', label: 'MRR', icon: 'fas fa-chart-line', color: '#0d6efd' },
    { key: 'arr', label: 'ARR', icon: 'fas fa-calendar-alt', color: '#198754' },
    { key: 'new_subscriptions', label: 'New Subs', icon: 'fas fa-plus-circle', color: '#20c997' },
    { key: 'canceled_subscriptions', label: 'Churned', icon: 'fas fa-minus-circle', color: '#dc3545' }
  ].filter(metric => data[metric.key as keyof SubscriptionData] && viewMode === 'absolute');

  // Calculate key metrics
  const currentMRR = data.mrr?.[data.mrr.length - 1]?.value || 0;
  const previousMRR = data.mrr?.[data.mrr.length - 2]?.value || 0;
  const mrrGrowth = previousMRR > 0 ? ((currentMRR - previousMRR) / previousMRR) * 100 : 0;

  if (error) {
    return (
      <div className={`mrr-chart ${className}`.trim()}>
        <div className="card">
          <div className="card-header">
            <h6 className="mb-0">
              <i className="fas fa-chart-line me-2" aria-hidden="true"></i>
              {title}
            </h6>
          </div>
          <div className="card-body">
            <div className="alert alert-danger">
              <i className="fas fa-exclamation-triangle me-2" aria-hidden="true"></i>
              <strong>Error:</strong> {error}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className={`mrr-chart ${className}`.trim()}>
        <div className="card">
          <div className="card-header">
            <h6 className="mb-0">
              <i className="fas fa-chart-line me-2" aria-hidden="true"></i>
              {title}
            </h6>
          </div>
          <div className="card-body">
            <AnalyticsChartSpinner height={height} message="Loading MRR data..." />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`mrr-chart ${className}`.trim()}>
      <div className="card">
        <div className="card-header">
          <div className="d-flex justify-content-between align-items-center flex-wrap">
            <div>
              <h6 className="mb-1">
                <i className="fas fa-chart-line me-2" aria-hidden="true"></i>
                {title}
              </h6>
              <div className="d-flex align-items-center gap-3">
                <span className="badge bg-primary fs-6">
                  {formatCurrency(currentMRR)}
                </span>
                <span className={`small ${mrrGrowth >= 0 ? 'text-success' : 'text-danger'}`}>
                  <i className={`fas fa-arrow-${mrrGrowth >= 0 ? 'up' : 'down'} me-1`} aria-hidden="true"></i>
                  {formatPercentage(mrrGrowth)} vs last month
                </span>
              </div>
            </div>
            
            {/* Controls */}
            <div className="d-flex gap-2 align-items-center flex-wrap">
              {/* View Mode Toggle */}
              <div className="btn-group" role="group" aria-label="View mode">
                <input
                  type="radio"
                  className="btn-check"
                  name="view-mode"
                  id="view-absolute"
                  value="absolute"
                  checked={viewMode === 'absolute'}
                  onChange={(e) => e.target.checked && setViewMode('absolute')}
                />
                <label className="btn btn-outline-primary btn-sm" htmlFor="view-absolute">
                  Revenue
                </label>

                <input
                  type="radio"
                  className="btn-check"
                  name="view-mode"
                  id="view-growth"
                  value="growth"
                  checked={viewMode === 'growth'}
                  onChange={(e) => e.target.checked && setViewMode('growth')}
                />
                <label className="btn btn-outline-primary btn-sm" htmlFor="view-growth">
                  Growth %
                </label>
              </div>

              {/* Metric Selection */}
              {viewMode === 'absolute' && (
                <div className="btn-group" role="group" aria-label="Select metrics">
                  {availableMetrics.map((metric) => (
                    <React.Fragment key={metric.key}>
                      <input
                        type="checkbox"
                        className="btn-check"
                        id={`metric-${metric.key}`}
                        checked={selectedMetrics.has(metric.key)}
                        onChange={() => handleMetricToggle(metric.key)}
                      />
                      <label
                        className={`btn btn-outline-secondary btn-sm ${selectedMetrics.has(metric.key) ? 'active' : ''}`}
                        htmlFor={`metric-${metric.key}`}
                        style={{
                          borderColor: selectedMetrics.has(metric.key) ? metric.color : undefined,
                          color: selectedMetrics.has(metric.key) ? metric.color : undefined
                        }}
                      >
                        <i className={`${metric.icon} me-1`} aria-hidden="true"></i>
                        {metric.label}
                      </label>
                    </React.Fragment>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="card-body">
          {chartData.datasets.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <i className="fas fa-chart-line fa-2x mb-3" aria-hidden="true"></i>
              <p className="mb-0">No MRR data available for the selected period</p>
            </div>
          ) : (
            <AnalyticsChart
              type={chartType}
              data={chartData}
              options={chartOptions}
              height={height}
              onDataPointClick={onDataPointClick}
              showLegend={false} // We handle legend in header
            />
          )}
        </div>
      </div>

      <style jsx>{`
        .mrr-chart .btn-group .btn-sm {
          padding: 0.25rem 0.5rem;
          font-size: 0.8rem;
        }

        .mrr-chart .btn-check:checked + .btn {
          background-color: var(--bs-primary);
          border-color: var(--bs-primary);
          color: white;
        }

        .mrr-chart .card-header {
          background-color: rgba(var(--bs-success-rgb), 0.05);
          border-bottom: 1px solid rgba(var(--bs-success-rgb), 0.15);
        }

        .mrr-chart .badge.fs-6 {
          font-size: 1rem !important;
        }

        @media (max-width: 768px) {
          .mrr-chart .card-header .d-flex {
            flex-direction: column;
            align-items: stretch;
            gap: 1rem;
          }

          .mrr-chart .btn-group {
            flex-wrap: wrap;
          }

          .mrr-chart .btn-group .btn-sm {
            flex: 1;
            min-width: auto;
          }
        }
      `}</style>
    </div>
  );
};

MRRChart.displayName = 'MRRChart';

export default MRRChart;